import { Button, Card, Flex, Segmented, Typography } from 'antd'
import { Copy, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CodeSnippet, useSnippetTipsStyles } from 'src/components'

import {
  N8N_TEMPLATE_URL,
  SIGNATURE_EXAMPLES,
  SIGNATURE_LANGUAGES,
  type SignatureLanguage
} from './constants'

const { Link, Text } = Typography

export const SignatureCard = () => {
  const { t } = useTranslation()
  const tipsClassName = useSnippetTipsStyles()
  const [language, setLanguage] = useState<SignatureLanguage>('Node.js')

  const example = SIGNATURE_EXAMPLES[language]

  return (
    <Card
      size="small"
      title={t(
        'workspace.apps.outboundWebhooks.verify.title',
        'Verifying deliveries'
      )}
      extra={
        <Flex gap={8} align="center">
          {/* Language names are proper nouns, so they are not translated. */}
          <Segmented
            size="small"
            value={language}
            onChange={(value) => setLanguage(value as SignatureLanguage)}
            options={[...SIGNATURE_LANGUAGES]}
          />
          <Button
            size="small"
            icon={<Copy size={14} />}
            onClick={() => void navigator.clipboard.writeText(example)}
          >
            {t('workspace.apps.outboundWebhooks.verify.copy', 'Copy')}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={12}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t(
            'workspace.apps.outboundWebhooks.verify.intro',
            'Every delivery carries X-CRM-Signature: t=<unix>,v1=<hex> — an HMAC-SHA256 of "<t>.<raw body>" using the signing secret for this webhook. Recomputing it is how the receiver knows the request came from us and was not altered.'
          )}
        </Text>

        <CodeSnippet>{example}</CodeSnippet>

        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(
            'workspace.apps.outboundWebhooks.verify.secretHint',
            'Take the secret from "Show signing secret" and keep it in your environment, not in code.'
          )}
        </Text>

        <ul className={tipsClassName}>
          <li>
            {t(
              'workspace.apps.outboundWebhooks.verify.tips.rawBody',
              'Sign the raw bytes. Parsing the JSON and serialising it again changes the bytes, and the signature will never match.'
            )}
          </li>
          <li>
            {t(
              'workspace.apps.outboundWebhooks.verify.tips.constantTime',
              'Compare with a constant-time function. A plain === leaks how much of the signature was correct.'
            )}
          </li>
          <li>
            {t(
              'workspace.apps.outboundWebhooks.verify.tips.replay',
              'Reject an old timestamp. Each retry is signed afresh, so a stale t means someone is replaying a captured request.'
            )}
          </li>
          <li>
            {t(
              'workspace.apps.outboundWebhooks.verify.tips.idempotency',
              'Deduplicate on X-CRM-Delivery. A delivery that timed out on your side is retried, so the same event can arrive twice.'
            )}
          </li>
          <li>
            {t(
              'workspace.apps.outboundWebhooks.verify.tips.retries',
              'Answer 2xx quickly and process afterwards. Anything else is retried five times over about two and a half hours, then dropped.'
            )}
          </li>
        </ul>

        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(
            'workspace.apps.outboundWebhooks.verify.n8n',
            'n8n needs adapting. The template below implements GitHub\'s scheme — one header, signed over the body alone. For us: turn on Raw Body on the Webhook node, split t and v1 out of X-CRM-Signature, and sign "<t>.<raw body>". The Crypto and IF nodes cannot prepend the timestamp to raw bytes, so put the Node.js snippet above in a Code node instead.'
          )}{' '}
          <Link href={N8N_TEMPLATE_URL} target="_blank" rel="noreferrer">
            {t(
              'workspace.apps.outboundWebhooks.verify.n8nLink',
              'n8n HMAC validation template'
            )}
            <ExternalLink
              size={12}
              style={{ marginLeft: 4, verticalAlign: -1 }}
            />
          </Link>
        </Text>
      </Flex>
    </Card>
  )
}
